import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, RotateCcw, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/Seo";

export default function StartHere() {
  const [quiz, setQuiz] = useState(null);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/quiz").then((res) => setQuiz(res.data));
  }, []);

  if (!quiz) return <div className="mx-auto max-w-3xl px-6 py-32 text-charcoal-500">Loading…</div>;

  const questions = quiz.questions || [];
  const total = questions.length;
  const current = questions[step];
  const progress = result ? 100 : Math.round((step / total) * 100);

  const choose = async (optId) => {
    const next = { ...answers, [current.id]: optId };
    setAnswers(next);
    if (step < total - 1) {
      setStep(step + 1);
    } else {
      setSubmitting(true);
      const { data } = await api.post("/quiz/submit", { answers: next });
      setResult(data);
      setSubmitting(false);
      window.scrollTo(0, 0);
    }
  };

  const restart = () => {
    setAnswers({});
    setStep(0);
    setResult(null);
    window.scrollTo(0, 0);
  };

  return (
    <div data-testid="page-start-here" className="mx-auto max-w-3xl px-6 lg:px-12 py-16 lg:py-24">
      <Seo
        title="Start Here — Your Move-to-Mexico Roadmap"
        description="Answer a few quick questions and get a personalized roadmap for your move from the US to Mexico — covering timeline, route, pets, residency, and more."
        path="/start-here"
      />
      <p className="text-sm uppercase tracking-[0.2em] text-clay-700 font-medium mb-3">Start Here</p>

      {/* Progress */}
      <div className="h-2 bg-sand-200 rounded-full overflow-hidden mb-10">
        <div
          data-testid="quiz-progress"
          className="bg-sage h-full transition-all duration-500 ease-in-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      {!result && (
        <div key={step} className="animate-fade-up">
          {step === 0 && (
            <p className="text-charcoal-600 mb-8 text-lg leading-relaxed">{quiz.intro}</p>
          )}
          <h1 className="text-3xl sm:text-4xl font-light text-charcoal-900 leading-tight mb-8">
            {current.text}
          </h1>
          <div className="space-y-4">
            {current.options.map((opt) => (
              <button
                key={opt.id}
                data-testid={`quiz-option-${current.id}-${opt.id}`}
                onClick={() => choose(opt.id)}
                disabled={submitting}
                className="group flex w-full items-center justify-between rounded-2xl border border-sand-200 bg-white p-6 text-left transition-all hover:-translate-y-0.5 hover:border-terracotta hover:shadow-[0_8px_24px_rgba(44,42,41,0.08)]"
              >
                <span className="text-lg text-charcoal-800">{opt.label}</span>
                <ArrowRight size={20} className="text-charcoal-300 transition-all group-hover:translate-x-1 group-hover:text-terracotta" />
              </button>
            ))}
          </div>
          <div className="mt-8 flex items-center justify-between text-sm text-charcoal-500">
            {step > 0 ? (
              <button data-testid="quiz-back" onClick={() => setStep(step - 1)} className="inline-flex items-center gap-1 hover:text-terracotta transition-colors">
                <ArrowLeft size={15} /> Back
              </button>
            ) : <span />}
            <span>Question {step + 1} of {total}</span>
          </div>
        </div>
      )}

      {result && (
        <div data-testid="quiz-result" className="animate-fade-up">
          <div className="flex items-center gap-3 mb-4">
            <CheckCircle2 className="text-sage" strokeWidth={1.5} />
            <h1 className="text-3xl sm:text-4xl font-light text-charcoal-900">Your starting point</h1>
          </div>
          <p className="text-lg text-charcoal-600 leading-relaxed mb-10">{result.intro}</p>

          <div className="space-y-4">
            {result.results.length === 0 && (
              <p className="text-charcoal-600">You're in great shape! Explore the guides or book a call to fine-tune your plan.</p>
            )}
            {result.results.map((r) => (
              <Link
                key={r.slug}
                to={`/${r.slug}`}
                data-testid={`quiz-result-${r.slug}`}
                className="group flex items-center justify-between rounded-2xl border border-sand-200 bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-terracotta"
              >
                <div>
                  <h3 className="text-lg font-medium text-charcoal-800">{r.nav_label}</h3>
                  <p className="text-charcoal-600 text-sm mt-1">{r.hero_subtitle}</p>
                </div>
                <ArrowRight size={20} className="shrink-0 ml-4 text-charcoal-300 transition-all group-hover:translate-x-1 group-hover:text-terracotta" />
              </Link>
            ))}
          </div>

          <div className="mt-12 rounded-3xl bg-sage-light p-8 lg:p-10 text-center">
            <p className="text-charcoal-700 text-lg leading-relaxed">{result.closing}</p>
            <Link to="/book" data-testid="quiz-book-cta" className="mt-6 inline-flex rounded-full bg-terracotta px-8 py-3 text-white font-medium hover:bg-terracotta-hover transition-colors">
              Book an intro call
            </Link>
          </div>

          <button data-testid="quiz-restart" onClick={restart} className="mt-8 inline-flex items-center gap-2 text-sm text-charcoal-500 hover:text-terracotta transition-colors">
            <RotateCcw size={15} /> Retake the quiz
          </button>
        </div>
      )}
    </div>
  );
}
